import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button, Dropdown, Flex, Modal, Typography } from "antd";
import type { MenuProps } from "antd";
import { ChevronDown, LogOut, TextAlignJustify, User, X } from "lucide-react";

import BrandLogo from "@/assets/logo/brand-logo.png";
import UserHeaderIcon from "@/assets/icons/user_header.svg";
import { NAV_ITEMS } from "@/constants/nav";
import { useAuthStore } from "@/stores/auth.store";
import { logout } from "@/services/auth.service";
import { useLoading } from "@/hooks/use-loading";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useTdaStore } from "@/stores/tda.store";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const user = useAuthStore((s) => s.user);
  const clearVerifiedTdaPassengers = useTdaStore(
    (state) => state.clearVerifiedTdaPassengers,
  );

  const { loading, withLoading } = useLoading();

  const handleLogout = async () => {
    const sellerId = user?.agentCompanySellerId;

    if (!sellerId) {
      toast.error("User ID not found. Logout failed.");
      return;
    }

    try {
      await withLoading(() =>
        logout({
          agentCompanySellerId: sellerId,
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Logout failed, please try again",
      );
    } finally {
      useAuthStore.getState().logout();
      setLogoutOpen(false);
      navigate("/auth", { replace: true });
    }
  };

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "logout") {
      setLogoutOpen(true);
      return;
    }

    if (key === "/purchase-ticket") {
      clearVerifiedTdaPassengers();
    }

    navigate(key);
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: "/profile",
      icon: <User size={18} className="text-primary" aria-hidden="true" />,
      label: "Profile",
    },
    {
      key: "logout",
      icon: <LogOut size={18} className="text-primary" aria-hidden="true" />,
      label: "Logout",
    },
  ];

  const mobileMenuItems: MenuProps["items"] = [
    ...NAV_ITEMS.map((item) => ({
      key: item.to,
      icon: <item.icon size={14} className="text-primary" aria-hidden="true" />,
      label: item.label,
    })),

    {
      type: "divider" as const,
    },

    {
      key: "logout",
      icon: <LogOut size={14} className="text-primary" aria-hidden="true" />,
      label: "Logout",
    },
  ];

  return (
    <>
      {/* Global Backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/10 backdrop-blur-sm md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <header className="sticky top-0 z-50 border-b border-white/15 bg-white/45 shadow-[0_4px_24px_rgba(0,0,0,0.25)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
          {/* Logo + Desktop Navigation */}
          <div className="flex items-center gap-8">
            <Link to="/inventory" className="flex items-center gap-1">
              <img
                src={BrandLogo}
                alt="Brand Logo"
                className="h-auto w-36 md:w-40"
              />
            </Link>

            <nav
              className="hidden items-center gap-8 md:flex"
              aria-label="Site navigation"
            >
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => {
                    if (item.to === "/purchase-ticket") {
                      clearVerifiedTdaPassengers();
                    }
                  }}
                  className={({ isActive }) =>
                    cn(
                      "relative pb-1 text-sm font-medium transition-colors md:font-semibold",
                      item.to === "/profile" && "md:hidden",

                      "after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full",
                      "after:origin-left after:bg-primary",
                      "after:transition-transform after:duration-300 after:ease-out",

                      isActive
                        ? "text-primary after:scale-x-100"
                        : [
                            "text-secondary-foreground md:text-muted-foreground",
                            "hover:text-primary",
                            "after:scale-x-0 hover:after:scale-x-100",
                          ],
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Desktop User Menu */}
          <Dropdown
            menu={{
              items: userMenuItems,
              selectedKeys: [location.pathname],
              onClick: handleMenuClick,
              className: "p-2! font-medium!",
            }}
            trigger={["click"]}
            placement="bottomRight"
            className="mt-2"
          >
            <button
              type="button"
              className="hidden cursor-pointer items-center gap-2 text-sm font-medium text-secondary-foreground transition-colors hover:text-primary md:flex"
            >
              <img
                src={UserHeaderIcon}
                alt=""
                className="h-5 w-5"
                aria-hidden="true"
              />

              <span>{user?.name ?? "-"}</span>

              <ChevronDown size={18} aria-hidden="true" />
            </button>
          </Dropdown>

          {/* Mobile Menu */}
          <Dropdown
            open={mobileMenuOpen}
            onOpenChange={setMobileMenuOpen}
            menu={{
              items: mobileMenuItems,
              selectedKeys: [location.pathname],
              onClick: handleMenuClick,
              className: "p-4! flex flex-col gap-3 font-medium!",
            }}
            trigger={["click"]}
            placement="bottomRight"
            styles={{
              root: {
                width: "calc(100vw - 2rem)",
              },
            }}
          >
            <Button
              type="text"
              aria-label="Toggle menu"
              className="relative z-50 flex items-center justify-center md:hidden!"
            >
              {mobileMenuOpen ? (
                <X size={18} aria-hidden="true" />
              ) : (
                <TextAlignJustify size={18} aria-hidden="true" />
              )}
            </Button>
          </Dropdown>
        </div>

        {/* Logout Modal */}
        <Modal
          open={logoutOpen}
          onCancel={() => {
            if (!loading) {
              setLogoutOpen(false);
            }
          }}
          closable={false}
          centered
          title={null}
          mask={{ blur: true }}
          footer={
            <Flex justify="center" gap="small" className="flex-col md:flex-row">
              <Button
                color="default"
                variant="solid"
                disabled={loading}
                onClick={() => setLogoutOpen(false)}
                className="w-full md:w-56"
                style={{
                  borderRadius: "12px",
                }}
              >
                Cancel
              </Button>

              <Button
                color="primary"
                variant="solid"
                loading={loading}
                onClick={handleLogout}
                className="w-full md:w-56"
                style={{
                  borderRadius: "12px",
                }}
              >
                {loading ? "Logging out..." : "Yes"}
              </Button>
            </Flex>
          }
        >
          <Typography
            style={{
              color: "#333333",
              fontSize: "18px",
              fontWeight: 600,
              textAlign: "center",
            }}
          >
            Log Out?
          </Typography>
        </Modal>
      </header>
    </>
  );
}
